import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, CircularProgress, Alert, Snackbar,
  Paper, Grid, InputAdornment, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem, Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import AdminPageWrapper from './components/AdminPageWrapper';
import ProfessionalTable from '../components/ProfessionalTable';
import { API, API_BASE_URL } from '../config/apiConfig';
import api from '../utils/api';
import { jwtDecode } from 'jwt-decode';

function AdminCountryKYC() {
  const [countries, setCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterKYC, setFilterKYC] = useState('all'); // 'all', 'required', 'not_required'
  const [filterRegion, setFilterRegion] = useState('all');

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [formData, setFormData] = useState({
    countryId: null,
    countryName: '',
    countryCode: '',
    isKYCRequired: true,
    requirementLevel: 'Mandatory',
    kycProvider: '',
    complianceRegion: '',
    minWithdrawalForKYC: '',
    notes: '',
    isActive: true,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [endpointNotAvailable, setEndpointNotAvailable] = useState(false);

  // Fetch all countries with KYC requirements
  const fetchCountries = useCallback(async () => {
    setLoading(true);
    try {
      // Verify token exists and check admin status
      const token = localStorage.getItem('token');
      if (!token) {
        setSnackbar({ 
          open: true, 
          message: 'Authentication required. Please log in again.', 
          severity: 'error' 
        });
        setLoading(false);
        return;
      }
      
      // Decode token to check admin status
      try {
        const decoded = jwtDecode(token);
        console.log('🔑 Token decoded:', decoded);
        console.log('👤 User Type ID:', decoded.userTypeId);
        console.log('👤 Is Admin (userTypeId === 1):', decoded.userTypeId === 1);
        if (decoded.userTypeId !== 1) {
          setSnackbar({ 
            open: true, 
            message: 'Admin access required. Your account does not have admin permissions.', 
            severity: 'error' 
          });
          setLoading(false);
          return;
        }
      } catch (decodeError) {
        console.error('❌ Error decoding token:', decodeError);
        setSnackbar({ 
          open: true, 
          message: 'Invalid token. Please log in again.', 
          severity: 'error' 
        });
        setLoading(false);
        return;
      }
      
      console.log('🔍 Fetching countries from:', API.adminCountryKYC);
      console.log('🔑 Token exists:', !!token);
      console.log('🔑 Full URL:', API.adminCountryKYC);
      
      const response = await api.get(API.adminCountryKYC);
      const data = response.data;
      
      console.log('📡 Response received:', data);
      
      if (data.success) {
        console.log('✅ Fetched country KYC requirements:', data.data.length);
        setCountries(data.data);
        setFilteredCountries(data.data);
      } else {
        setSnackbar({ open: true, message: data.message || 'Failed to load countries', severity: 'error' });
      }
    } catch (error) {
      console.error('❌ Error fetching countries:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error response data:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      
      // Extract detailed error message from backend
      let errorMessage = 'Failed to load countries';
      if (error.response?.data?.error) {
        // Backend returns error in 'error' field: "Forbidden: admin access only"
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // For 403 errors, provide helpful guidance
      if (error.response?.status === 403) {
        // Check if token shows admin but backend rejects
        try {
          const token = localStorage.getItem('token');
          if (token) {
            const decoded = jwtDecode(token);
            if (decoded.userTypeId === 1) {
              // Token says admin but backend rejects - likely endpoint doesn't exist
              errorMessage = 'This feature is not yet available. The backend endpoint for Country KYC management may not be implemented.';
              setEndpointNotAvailable(true); // Show "Feature Coming Soon" UI
            } else {
              // Token doesn't show admin
              errorMessage = 'Admin access required. Your account does not have admin permissions.';
            }
          }
        } catch (e) {
          // If we can't decode, use the backend message
          if (!errorMessage.includes('admin') && !errorMessage.includes('not yet available')) {
            errorMessage = 'Access forbidden. ' + errorMessage;
          }
        }
      }
      
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  // Filter countries based on search and filters
  useEffect(() => {
    let filtered = countries;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(country =>
        country.CountryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.CountryCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.ComplianceRegion?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // KYC filter
    if (filterKYC === 'required') {
      filtered = filtered.filter(c => c.IsKYCRequired === true);
    } else if (filterKYC === 'not_required') {
      filtered = filtered.filter(c => c.IsKYCRequired === false);
    }

    // Region filter
    if (filterRegion !== 'all') {
      filtered = filtered.filter(c => c.ComplianceRegion === filterRegion);
    }

    setFilteredCountries(filtered);
  }, [searchTerm, filterKYC, filterRegion, countries]);

  // Get unique regions for filter
  const uniqueRegions = useMemo(() => {
    const regions = countries
      .map(c => c.ComplianceRegion)
      .filter((region, index, self) => region && self.indexOf(region) === index);
    return regions.sort();
  }, [countries]);

  // Open edit dialog
  const handleEdit = (country) => {
    setEditingCountry(country);
    setFormData({
      countryId: country.CountryID,
      countryName: country.CountryName,
      countryCode: country.CountryCode,
      isKYCRequired: country.IsKYCRequired ?? true,
      requirementLevel: country.RequirementLevel || 'Mandatory',
      kycProvider: country.KYCProvider || '',
      complianceRegion: country.ComplianceRegion || '',
      minWithdrawalForKYC: country.MinWithdrawalForKYC || '',
      notes: country.Notes || '',
      isActive: country.IsActive !== false,
    });
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCountry(null);
    setFormData({
      countryId: null,
      countryName: '',
      countryCode: '',
      isKYCRequired: true,
      requirementLevel: 'Mandatory',
      kycProvider: '',
      complianceRegion: '',
      minWithdrawalForKYC: '',
      notes: '',
      isActive: true,
    });
  };

  // Save KYC requirement
  const handleSave = async () => {
    setSaving(true);
    
    const payload = {
      countryId: formData.countryId,
      isKYCRequired: formData.isKYCRequired,
      requirementLevel: formData.requirementLevel,
      kycProvider: formData.kycProvider || null,
      complianceRegion: formData.complianceRegion || null,
      minWithdrawalForKYC: formData.minWithdrawalForKYC ? parseFloat(formData.minWithdrawalForKYC) : null,
      notes: formData.notes || null,
      isActive: formData.isActive,
    };
    
    console.log('💾 Saving KYC requirement:', payload);
    
    try {
      const response = await api.post(API.adminCountryKYC, payload);
      const data = response.data;

      console.log('📡 Response data:', data);

      if (data.success) {
        setSnackbar({ open: true, message: 'KYC requirement saved successfully', severity: 'success' });
        handleCloseDialog();
        await fetchCountries(); // Refresh list
      } else {
        console.error('❌ Save failed:', data.message);
        setSnackbar({ open: true, message: data.message || 'Failed to save KYC requirement', severity: 'error' });
      }
    } catch (error) {
      console.error('❌ Error saving KYC requirement:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save KYC requirement';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Get requirement level color
  const getRequirementLevelColor = (level) => {
    switch (level) {
      case 'Mandatory':
        return 'error';
      case 'Optional':
        return 'warning';
      case 'NotRequired':
        return 'success';
      default:
        return 'default';
    }
  };

  // Show "Feature Not Available" UI if endpoint doesn't exist
  if (endpointNotAvailable && !loading) {
    return (
      <AdminPageWrapper>
        <Box>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h4" fontWeight={600}>
                Country KYC Requirements
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Manage KYC verification requirements for each country
              </Typography>
            </Box>
          </Box>

          {/* Feature Not Available Message */}
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              bgcolor: 'background.paper',
              border: '2px dashed',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Feature Coming Soon
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                The Country KYC management feature is currently being developed. The backend endpoint for this feature needs to be implemented.
              </Typography>
              <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto', textAlign: 'left' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Backend Endpoint Required:
                </Typography>
                <Typography variant="body2" component="code" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  GET /api/admin/country-kyc
                </Typography>
                <Typography variant="body2" component="code" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', display: 'block', mt: 1 }}>
                  POST /api/admin/country-kyc
                </Typography>
                <Typography variant="body2" sx={{ mt: 2, fontSize: '0.875rem' }}>
                  Please contact the backend development team to implement this endpoint.
                </Typography>
              </Alert>
            </Box>
          </Paper>
        </Box>
      </AdminPageWrapper>
    );
  }

  return (
    <AdminPageWrapper>
      <Box>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" fontWeight={600}>
              Country KYC Requirements
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Manage KYC verification requirements for each country
            </Typography>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by country name, code, or region..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>KYC Status</InputLabel>
                <Select
                  value={filterKYC}
                  onChange={(e) => setFilterKYC(e.target.value)}
                  label="KYC Status"
                >
                  <MenuItem value="all">All Countries</MenuItem>
                  <MenuItem value="required">KYC Required</MenuItem>
                  <MenuItem value="not_required">KYC Not Required</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Compliance Region</InputLabel>
                <Select
                  value={filterRegion}
                  onChange={(e) => setFilterRegion(e.target.value)}
                  label="Compliance Region"
                >
                  <MenuItem value="all">All Regions</MenuItem>
                  {uniqueRegions.map(region => (
                    <MenuItem key={region} value={region}>{region}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="body2" color="text.secondary" textAlign="right">
                Showing {filteredCountries.length} of {countries.length}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <ProfessionalTable
          columns={[
            {
              header: 'Country',
              accessor: 'CountryName',
              render: (value, row) => (
                <Box>
                  <Typography fontWeight={500}>{value}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {row.CountryCode || '—'}
                  </Typography>
                </Box>
              ),
            },
            {
              header: 'KYC Required',
              accessor: 'IsKYCRequired',
              render: (value) => (
                <Chip
                  label={value ? 'Required' : 'Not Required'}
                  color={value ? 'error' : 'success'}
                  size="small"
                />
              ),
              style: { textAlign: 'center' },
            },
            {
              header: 'Requirement Level',
              accessor: 'RequirementLevel',
              render: (value) => (
                <Chip
                  label={value || 'Mandatory'}
                  color={getRequirementLevelColor(value)}
                  size="small"
                  variant="outlined"
                />
              ),
              style: { textAlign: 'center' },
            },
            {
              header: 'Compliance Region',
              accessor: 'ComplianceRegion',
              render: (value) => (
                <Typography variant="body2">{value || '—'}</Typography>
              ),
              style: { textAlign: 'center' },
            },
            {
              header: 'KYC Provider',
              accessor: 'KYCProvider',
              render: (value) => (
                <Typography variant="body2">{value || '—'}</Typography>
              ),
              style: { textAlign: 'center' },
            },
            {
              header: 'Min Withdrawal',
              accessor: 'MinWithdrawalForKYC',
              render: (value) => (
                <Typography variant="body2">
                  {value ? `$${parseFloat(value).toFixed(2)}` : '—'}
                </Typography>
              ),
              style: { textAlign: 'right' },
            },
            {
              header: 'Status',
              accessor: 'IsActive',
              render: (value) => (
                <Chip
                  label={value !== false ? 'Active' : 'Inactive'}
                  color={value !== false ? 'success' : 'default'}
                  size="small"
                />
              ),
              style: { textAlign: 'center' },
            },
            {
              header: 'Actions',
              accessor: 'CountryID',
              render: (value, row) => (
                <Tooltip title="Edit KYC Settings">
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(row)}
                    color="primary"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ),
              style: { textAlign: 'center' },
            },
          ]}
          data={filteredCountries.map(country => ({ ...country, id: country.CountryID }))}
          onRowClick={(country) => handleEdit(country)}
          loading={loading}
          emptyMessage="No countries found"
        />

        {/* Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            Edit KYC Requirements - {formData.countryName} ({formData.countryCode})
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* KYC Required Toggle */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isKYCRequired}
                      onChange={(e) => setFormData({ ...formData, isKYCRequired: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight={500}>KYC Required</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Require users from this country to complete KYC verification
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

              {/* Requirement Level */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Requirement Level</InputLabel>
                  <Select
                    value={formData.requirementLevel}
                    onChange={(e) => setFormData({ ...formData, requirementLevel: e.target.value })}
                    label="Requirement Level"
                  >
                    <MenuItem value="Mandatory">Mandatory</MenuItem>
                    <MenuItem value="Optional">Optional</MenuItem>
                    <MenuItem value="NotRequired">Not Required</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* KYC Provider */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="KYC Provider"
                  value={formData.kycProvider}
                  onChange={(e) => setFormData({ ...formData, kycProvider: e.target.value })}
                  placeholder="e.g., Stripe, Sumsub, Manual"
                />
              </Grid>

              {/* Compliance Region */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Compliance Region"
                  value={formData.complianceRegion}
                  onChange={(e) => setFormData({ ...formData, complianceRegion: e.target.value })}
                  placeholder="e.g., EU, North America, Asia-Pacific"
                />
              </Grid>

              {/* Min Withdrawal for KYC */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Minimum Withdrawal for KYC"
                  type="number"
                  value={formData.minWithdrawalForKYC}
                  onChange={(e) => setFormData({ ...formData, minWithdrawalForKYC: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  placeholder="Leave empty for no threshold"
                />
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes or comments about this country's KYC requirements"
                />
              </Grid>

              {/* Active Status */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight={500}>Active</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Temporarily disable this KYC requirement without deleting it
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminPageWrapper>
  );
}

export default AdminCountryKYC;

